import { useEffect, useState } from 'react';

// IndexedDB store for offline reading: saved books (meta + chapters) and
// a queue of writes made while offline (progress, quotes), flushed when
// the network is back. No library: the API surface we need is tiny.

const DB_NAME = 'wowreader';
const DB_VERSION = 2; // 2: images store

const open = () => new Promise((resolve, reject) => {
  if (typeof indexedDB === 'undefined') { reject(new Error('no indexedDB')); return; }
  const req = indexedDB.open(DB_NAME, DB_VERSION);
  req.onupgradeneeded = () => {
    const db = req.result;
    if (!db.objectStoreNames.contains('texts')) db.createObjectStore('texts', { keyPath: 'id' });
    if (!db.objectStoreNames.contains('chapters')) db.createObjectStore('chapters', { keyPath: 'key' });
    if (!db.objectStoreNames.contains('queue')) db.createObjectStore('queue', { keyPath: 'seq', autoIncrement: true });
    if (!db.objectStoreNames.contains('images')) db.createObjectStore('images', { keyPath: 'key' });
  };
  req.onsuccess = () => resolve(req.result);
  req.onerror = () => reject(req.error);
});

const tx = async (store, mode, fn) => {
  const db = await open();
  return new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    const s = t.objectStore(store);
    let result;
    try { result = fn(s); } catch (e) { reject(e); return; }
    t.oncomplete = () => { db.close(); resolve(result && result.result !== undefined ? result.result : result); };
    t.onerror = () => { db.close(); reject(t.error); };
  });
};
const reqResult = (store, mode, fn) => tx(store, mode, (s) => fn(s));
const getAll = (store) => new Promise(async (resolve, reject) => {
  try {
    const db = await open();
    const t = db.transaction(store, 'readonly');
    const req = t.objectStore(store).getAll();
    req.onsuccess = () => { db.close(); resolve(req.result || []); };
    req.onerror = () => { db.close(); reject(req.error); };
  } catch (e) { reject(e); }
});
const getOne = (store, key) => new Promise(async (resolve, reject) => {
  try {
    const db = await open();
    const t = db.transaction(store, 'readonly');
    const req = t.objectStore(store).get(key);
    req.onsuccess = () => { db.close(); resolve(req.result || null); };
    req.onerror = () => { db.close(); reject(req.error); };
  } catch (e) { reject(e); }
});

const safe = (p, fallback) => p.catch(() => fallback);

// ---- saved texts ----------------------------------------------------
// meta: { id, artId, artName, author, cover, label, chapters, paragraphCount, savedAt, complete, progress }
export const getSavedText = (id) => safe(getOne('texts', id), null);
export const listSavedTexts = () => safe(getAll('texts'), []);
export const putSavedText = (meta) => safe(reqResult('texts', 'readwrite', (s) => s.put(meta)), null);
export const deleteSavedText = async (id, chapterCount) => {
  await safe(reqResult('texts', 'readwrite', (s) => s.delete(id)), null);
  // Images referenced by the stored chapters go with them.
  const names = [];
  for (let i = 0; i < chapterCount; i++) {
    const ps = await getChapter(id, i);
    if (ps) for (const p of ps) { const n = imageNameOf(p); if (n) names.push(n); }
  }
  if (names.length) await safe(tx('images', 'readwrite', (s) => { for (const n of names) s.delete(`${id}/${n}`); }), null);
  await safe(tx('chapters', 'readwrite', (s) => { for (let i = 0; i < chapterCount; i++) s.delete(`${id}:${i}`); }), null);
};

// ---- paragraph markers (mirror of logic/booktext/image.go) -----------
// "{{img:003.jpg}}" is an image paragraph; "{{ocr}}…" is text kept for
// search but not shown (the OCR under a scanned page).
const IMAGE_RE = /^\{\{img:([0-9]{3,4}\.(?:jpg|png|gif|webp))\}\}$/;
export const HIDDEN_PREFIX = '{{ocr}}';
export const imageNameOf = (p) => { const m = typeof p === 'string' && p.match(IMAGE_RE); return m ? m[1] : null; };
export const isHiddenParagraph = (p) => typeof p === 'string' && p.startsWith(HIDDEN_PREFIX);
export const searchableText = (p) => (imageNameOf(p) ? '' : (isHiddenParagraph(p) ? p.slice(HIDDEN_PREFIX.length) : p));

// ---- images of saved texts -------------------------------------------
export const getImage = (id, name) => safe(getOne('images', `${id}/${name}`).then((r) => (r ? r.blob : null)), null);
export const putImage = (id, name, blob) => safe(reqResult('images', 'readwrite', (s) => s.put({ key: `${id}/${name}`, blob })), null);
export const getChapter = (id, idx) => safe(getOne('chapters', `${id}:${idx}`).then((r) => (r ? r.paragraphs : null)), null);
export const putChapter = (id, idx, paragraphs) => safe(reqResult('chapters', 'readwrite', (s) => s.put({ key: `${id}:${idx}`, paragraphs })), null);

// ---- offline write queue ---------------------------------------------
// ops: { type: 'progress', textId, position } | { type: 'quote', artId, text }
export const enqueue = (op) => safe(reqResult('queue', 'readwrite', (s) => s.add({ ...op, at: Date.now() })), null);
export const flushQueue = async (axiosInstance) => {
  const ops = await safe(getAll('queue'), []);
  if (!ops.length) return 0;
  // Last progress per text wins; quotes all go.
  const lastProgress = {};
  const quotes = [];
  for (const op of ops) {
    if (op.type === 'progress') lastProgress[op.textId] = op;
    if (op.type === 'quote') quotes.push(op);
  }
  let done = 0;
  for (const op of Object.values(lastProgress)) {
    try { await axiosInstance.put(`/texts/${op.textId}/progress`, { position: op.position }); done++; } catch (e) { return done; }
  }
  for (const op of quotes) {
    try { await axiosInstance.post(`/arts/${op.artId}/myQuotes`, { text: op.text, isPublic: false }); done++; } catch (e) { return done; }
  }
  await safe(tx('queue', 'readwrite', (s) => s.clear()), null);
  return done;
};

// ---- online state -----------------------------------------------------
// The browser's online flag can be true while nothing gets through (a VPN
// whose link is gone keeps requests waiting instead of failing). The login
// bootstrap marks that state here after its timeout, and the reader and the
// save button then treat it as offline without waiting again.
let netDown = false;
export const setNetDown = (v) => {
  if (netDown === !!v) return;
  netDown = !!v;
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('netdown-change'));
};
export const isOnline = () => (typeof navigator === 'undefined' ? true : navigator.onLine !== false && !netDown);
export const useOnline = () => {
  const [online, setOnline] = useState(isOnline());
  useEffect(() => {
    const sync = () => setOnline(isOnline());
    window.addEventListener('online', sync);
    window.addEventListener('offline', sync);
    window.addEventListener('netdown-change', sync);
    return () => { window.removeEventListener('online', sync); window.removeEventListener('offline', sync); window.removeEventListener('netdown-change', sync); };
  }, []);
  return online;
};
